import React, { useCallback, useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import T from './theme';
import CollapsibleCard from './CollapsibleCard';
import ApproximationCard from './ApproximationCard';
import MyFoods, { MACRO_FIELDS, macroInvalid } from './MyFoods';

const FUEL_DISCLAIMER =
  'Not medical advice. Educational estimates only — not medical nutrition therapy. Use at your own risk.';

function formatStart(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

// Chip quantities: quarter steps at or below one serving, whole steps above.
export function stepQty(current, direction) {
  const q = Number(current) || 0;
  if (direction < 0) {
    const step = q <= 1 ? 0.25 : 1;
    return Math.max(0.25, Math.round((q - step) * 100) / 100);
  }
  const step = q < 1 ? 0.25 : 1;
  return Math.round((q + step) * 100) / 100;
}

// Removed chips (qty 0) drop out of the saved sentence entirely.
export function reconstructMealText(items, unmatched, fallback) {
  const bits = (items || []).filter((item) => Number(item.qty) > 0).map((item) => {
    const qty = Number(item.qty) || 1;
    const count = Number.isInteger(qty) ? qty : Math.round(qty * 100) / 100;
    return `${count} ${item.name}`;
  });
  (unmatched || []).forEach((raw) => bits.push(raw));
  // Everything removed means nothing to save, not "save the original".
  if (!bits.length && (items || []).length) return '';
  return bits.join(', ') || fallback;
}

function roundNutrient(value) {
  const n = Number(value) || 0;
  return Math.round(n * 10) / 10;
}

// What is left of today's targets, per macro, with "over" when past it.
export function macroLeft(targets, consumed) {
  const t = targets || {};
  const c = consumed || {};
  const row = (key) => {
    const target = Math.round(Number(t[key]) || 0);
    const eaten = Math.round(Number(c[key]) || 0);
    const diff = target - eaten;
    return { target, eaten, left: Math.max(0, diff), over: Math.max(0, -diff) };
  };
  return { kcal: row('kcal'), cho_g: row('cho_g'), pro_g: row('pro_g'), fat_g: row('fat_g') };
}

export function leftText(row, unit = ' g') {
  if (!row) return '';
  return row.over > 0 ? `+${row.over}${unit} over` : `${row.left}${unit} left`;
}

// Draws "133 g left" / "+5 g over" above each stacked bar. Inline, so no
// chartjs-plugin-datalabels dependency. Reads the numbers from chart options.
const leftLabelsPlugin = {
  id: 'umaxLeftLabels',
  afterDatasetsDraw(chart) {
    const labels = chart.options?.plugins?.umaxLeftLabels?.labels;
    if (!labels) return;
    const { ctx } = chart;
    const metas = chart.data.datasets.map((_, i) => chart.getDatasetMeta(i));
    ctx.save();
    ctx.font = '600 12px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    labels.forEach((text, i) => {
      const tops = metas.map((m) => m.data[i]).filter(Boolean).map((el) => el.y);
      const x = metas[0]?.data[i]?.x;
      if (x == null || !tops.length) return;
      ctx.fillStyle = text.startsWith('+') ? '#b00020' : '#333';
      ctx.fillText(text, x, Math.min(...tops) - 4);
    });
    ctx.restore();
  },
};

// Per one of the item's portions, from a resolved (already scaled) item.
export function perPortion(item) {
  const n = Number(item?.qty) > 0 ? Number(item.qty) : 1;
  return Object.fromEntries(
    MACRO_FIELDS.map(([k]) => [k, Math.round(((Number(item?.[k]) || 0) / n) * 10) / 10])
  );
}

// "fix" on any chip: the athlete's own label for this food. Saved as theirs
// (confirmed), it beats the house catalog and USDA from then on.
export function LabelEditor({ api, item, onSaved, onCancel }) {
  const [portion, setPortion] = useState(item.portion_label || '1 serving');
  const [draft, setDraft] = useState(perPortion(item));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const name = item.query || item.raw || item.name;
  const invalid = MACRO_FIELDS.some(([k]) => macroInvalid(draft[k]));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const macros = Object.fromEntries(
        MACRO_FIELDS.map(([k]) => [k, draft[k] === '' ? 0 : Number(draft[k])])
      );
      await api.post('/nutrition/estimates', {
        name,
        unit: item.query_unit || null,
        portion_label: portion,
        macros,
      });
      await onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save your label');
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="label-editor"
      style={{ border: `1px solid ${T.color.border}`, borderRadius: 8, padding: 8, margin: '4px 0 8px', fontSize: T.font.sm }}
    >
      <strong>Your label for “{name}”</strong>
      <div style={{ color: T.color.textMuted, fontSize: 11 }}>
        Per portion. Saved as yours: it wins over the house list and USDA next time.
      </div>
      <label style={{ display: 'block', margin: '6px 0 4px' }}>
        Portion
        <input
          value={portion}
          onChange={(e) => setPortion(e.target.value)}
          aria-label="label portion"
          style={{ display: 'block', width: 180 }}
        />
      </label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {MACRO_FIELDS.map(([k, label]) => (
          <label key={k} style={{ fontSize: 11 }}>
            {label}
            <input
              inputMode="decimal"
              value={draft[k]}
              onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
              aria-label={`label ${label}`}
              aria-invalid={macroInvalid(draft[k])}
              style={{ display: 'block', width: 64 }}
            />
          </label>
        ))}
      </div>
      {error && <p role="alert" style={{ color: T.color.danger, margin: '4px 0' }}>{error}</p>}
      <p style={{ margin: '6px 0 0' }}>
        <button type="button" onClick={save} disabled={busy || invalid}>Save my label</button>{' '}
        <button type="button" onClick={onCancel} disabled={busy}>Cancel</button>
      </p>
    </div>
  );
}

const RUN_CONTEXTS = { pre: 'pre-run', during: 'during run', post: 'post-run' };

export function mealTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export default function FuelCard({ api, weatherData, defaultMass = 70, defaultGut = 'med', refreshKey = 0 }) {
  const [massKg, setMassKg] = useState(defaultMass);
  const [durationH, setDurationH] = useState(1);
  const [workoutType, setWorkoutType] = useState('long');
  const [gut, setGut] = useState(defaultGut || 'med');
  // No meal picker: the server tags pre / during / post from the runs and
  // everything else is a timestamped meal.
  const context = 'auto';
  const [text, setText] = useState('');
  const [today, setToday] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [meals, setMeals] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showOverrides, setShowOverrides] = useState(false);
  const [qtys, setQtys] = useState({});
  const [approx, setApprox] = useState(null);
  const [showMyFoods, setShowMyFoods] = useState(false);
  const [labelFor, setLabelFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (defaultMass) setMassKg(defaultMass);
  }, [defaultMass]);

  const reloadToday = useCallback(async () => {
    try {
      const [td, ml, sg] = await Promise.all([
        api.get('/nutrition/today'),
        api.get('/nutrition/meals'),
        api.get('/nutrition/suggestions'),
      ]);
      setToday(td.data);
      setMeals(ml.data?.meals || []);
      setSuggestions(sg.data?.workouts || []);
    } catch (err) {
      /* guest / not ready */
    }
  }, [api]);

  // refreshKey bumps when Settings save (e.g. a new weight loss rate).
  useEffect(() => {
    reloadToday();
  }, [reloadToday, refreshKey]);

  const sessionParams = () => ({
    mass_kg: Number(massKg) || 70,
    duration_h: Number(durationH) || 1,
    workout_type: workoutType,
    gut_tolerance: gut,
  });

  const runEstimate = async (save, textOverride) => {
    const raw = (textOverride != null ? textOverride : text).trim();
    if (!raw) {
      setError('Type what you ate.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const path = save ? '/nutrition/meals' : '/nutrition/estimate';
      const res = await api.post(path, {
        text: raw,
        context,
        ...sessionParams(),
      });
      const body = save ? res.data.meal : res.data;
      setEstimate(body);
      const next = {};
      (body.items || []).forEach((item, i) => {
        next[i] = item.qty;
      });
      setQtys(next);
      if (save) {
        setText('');
        setEstimate(null);
        if (res.data.today) setToday(res.data.today);
        await reloadToday();
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Estimate failed');
    } finally {
      setBusy(false);
    }
  };

  const removeMeal = async (id) => {
    await api.delete(`/nutrition/meals/${id}`);
    await reloadToday();
  };

  // "Mom's chili" → ingredients → a card to accept. One model call per new dish.
  const approximate = async (dishText) => {
    const raw = (dishText != null ? dishText : text).trim();
    if (!raw) {
      setError('Name the dish.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/nutrition/approximate', { text: raw, context });
      setApprox({ id: res.data.id, card: res.data.card });
      setEstimate(null);
      if (res.data.today) setToday(res.data.today);
      await reloadToday();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Approximation failed');
    } finally {
      setBusy(false);
    }
  };

  const closeApprox = async (data) => {
    setApprox(null);
    setText('');
    if (data?.today) setToday(data.today);
    await reloadToday();
  };

  const targets = today?.targets || { cho_g: 0, pro_g: 0, fat_g: 0 };
  const consumed = today?.consumed || { cho_g: 0, pro_g: 0, fat_g: 0 };
  const remaining = today?.remaining || { cho_g: 0, pro_g: 0, fat_g: 0 };
  const barData = {
    labels: ['Carbs (g)', 'Protein (g)', 'Fat (g)'],
    datasets: [
      {
        label: 'consumed',
        data: [consumed.cho_g || 0, consumed.pro_g || 0, consumed.fat_g || 0],
        backgroundColor: '#4e79a7',
        stack: 't',
      },
      {
        label: 'remaining',
        data: [remaining.cho_g || 0, remaining.pro_g || 0, remaining.fat_g || 0],
        backgroundColor: '#d4d4d4',
        stack: 't',
      },
    ],
  };
  const left = macroLeft(targets, consumed);
  const barOptions = {
    responsive: true,
    layout: { padding: { top: 18 } },
    plugins: {
      legend: { position: 'bottom' },
      title: {
        display: true,
        text: `Target ${targets.cho_g || 0} / ${targets.pro_g || 0} / ${targets.fat_g || 0} g`,
      },
      umaxLeftLabels: {
        labels: [leftText(left.cho_g), leftText(left.pro_g), leftText(left.fat_g)],
      },
    },
    scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
  };

  const scaledItems = (estimate?.items || []).map((item, i) => {
    // A numeric 0 means removed (the × button). An empty or unreadable box
    // mid-typing keeps the parsed amount so the chip does not vanish.
    const typed = qtys[i];
    const raw = typed == null || typed === '' ? Number(item.qty) : Number(typed);
    const qty = Number.isFinite(raw) && raw >= 0 ? raw : item.qty;
    const factor = item.qty ? qty / item.qty : 1;
    return {
      ...item,
      qty,
      kcal: (item.kcal || 0) * factor,
      cho_g: (item.cho_g || 0) * factor,
      pro_g: (item.pro_g || 0) * factor,
      fat_g: (item.fat_g || 0) * factor,
      na_mg: (item.na_mg || 0) * factor,
      fiber_g: (item.fiber_g || 0) * factor,
    };
  });
  const scaledTotals = scaledItems.reduce(
    (acc, item) => ({
      kcal: acc.kcal + (item.kcal || 0),
      cho_g: acc.cho_g + (item.cho_g || 0),
      pro_g: acc.pro_g + (item.pro_g || 0),
      fat_g: acc.fat_g + (item.fat_g || 0),
    }),
    { kcal: 0, cho_g: 0, pro_g: 0, fat_g: 0 }
  );

  const bumpQty = (index, delta) => {
    setQtys((prev) => {
      const current = Number(prev[index] != null ? prev[index] : estimate?.items?.[index]?.qty) || 1;
      return { ...prev, [index]: stepQty(current, delta) };
    });
  };

  const setQty = (index, value) => {
    setQtys((prev) => ({ ...prev, [index]: value }));
  };

  const removeItem = (index) => {
    setQtys((prev) => ({ ...prev, [index]: 0 }));
  };

  const acceptSuggestion = (raw, name) => {
    const next = text.replace(raw, name);
    setText(next);
    runEstimate(false, next);
  };

  return (
    <CollapsibleCard title="Nutrition" accentColor={T.color.accentNutrition}>
      <p style={{ fontSize: T.font.sm, color: T.color.textMuted, marginTop: 0 }}>{FUEL_DISCLAIMER}</p>
      <p style={{ fontSize: T.font.sm, color: T.color.textMuted }}>
        Looking for a dietitian?{' '}
        <a href="https://www.usenourish.com/" target="_blank" rel="noreferrer">Nourish</a>
        {' '}is a good place to find one.
      </p>

      <h3 style={{ marginBottom: 6 }}>Today</h3>
      {today ? (
        <p style={{ fontSize: 14, marginTop: 0 }}>
          {(today.load?.shape || 'rest').replace(/^./, (c) => c.toUpperCase())} day
          {today.load?.workout_kcal ? ` · ${today.load.workout_kcal} kcal from workouts` : ''}
          {' · '}{today.tee_kcal} kcal total expenditure
          {today.incomplete ? ' · some items not counted' : ''}
          {today.pending_items
            ? ` · ${today.pending_items} item${today.pending_items === 1 ? '' : 's'} awaiting approval`
            : ''}
        </p>
      ) : (
        <p style={{ color: T.color.textLight, fontSize: T.font.sm }}>Log in to see today’s nutrition.</p>
      )}
      {today?.goal && (today.goal.mode === 'cut' || today.goal.phase) && (
        <p style={{ fontSize: T.font.sm, margin: '0 0 6px' }} data-testid="goal-line">
          {today.goal.mode === 'cut' ? `Losing ${today.goal.rate_lb_wk} lb/week` : 'Maintaining'}
          {today.goal.deficit_kcal > 0 ? ` · −${today.goal.deficit_kcal} kcal today` : ''}
          {today.goal.surplus_kcal > 0 ? ` · +${today.goal.surplus_kcal} kcal to fuel` : ''}
          {today.goal.reason ? (
            <span style={{ display: 'block', color: T.color.textMuted }}>{today.goal.reason}</span>
          ) : null}
        </p>
      )}
      {today && (
        <div data-testid="left-summary" style={{ margin: '4px 0 6px' }}>
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            {left.kcal.over > 0
              ? `${left.kcal.over.toLocaleString()} kcal over`
              : `${left.kcal.left.toLocaleString()} kcal left`}
            <span style={{ fontSize: T.font.sm, fontWeight: 400, color: T.color.textMuted }}>
              {' '}of {left.kcal.target.toLocaleString()}
            </span>
          </div>
          <div style={{ fontSize: T.font.sm, color: T.color.textMuted }}>
            Carbs {leftText(left.cho_g)} · Protein {leftText(left.pro_g)} · Fat {leftText(left.fat_g)}
          </div>
        </div>
      )}
      <div style={{ maxWidth: 520, marginBottom: 12 }}>
        <Bar data={barData} options={barOptions} plugins={[leftLabelsPlugin]} />
      </div>

      <h3>Log</h3>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="saltines, two pieces of toast, one egg"
        rows={3}
        style={{ width: '100%', boxSizing: 'border-box' }}
      />
      <p style={{ fontSize: T.font.xs, color: T.color.textLight, margin: '4px 0 8px' }}>
        Quantity is servings (5 crackers, 1 cup, 1 oz). After estimating, adjust quantities with the +/− buttons or tap a suggestion for anything unmatched.
      </p>
      <p>
        <button type="button" onClick={() => runEstimate(false)} disabled={busy}>Estimate</button>
        {' '}
        <button
          type="button"
          onClick={() => {
            const qtyEdited = Boolean(
              estimate &&
              scaledItems.some((item, i) => Number(item.qty) !== Number(estimate.items[i]?.qty))
            );
            const payload = qtyEdited
              ? reconstructMealText(scaledItems, estimate?.unmatched, text.trim())
              : text.trim();
            runEstimate(true, payload);
          }}
          disabled={busy}
        >
          Save meal
        </button>
        {' '}
        <button type="button" onClick={() => approximate()} disabled={busy}>
          Break down a dish
        </button>
        {' '}
        <button type="button" onClick={() => setShowMyFoods((o) => !o)} aria-expanded={showMyFoods}>
          {showMyFoods ? 'Hide my foods' : 'My foods'}
        </button>
      </p>
      {showMyFoods && <MyFoods api={api} onClose={() => setShowMyFoods(false)} />}
      {approx && (
        <ApproximationCard
          key={approx.id}
          api={api}
          mealId={approx.id}
          card={approx.card}
          onAccepted={closeApprox}
          onCancelled={closeApprox}
        />
      )}
      {estimate && (
        <div>
          {estimate.copy?.meal_summary && (
            <p style={{ fontSize: 14 }}>{estimate.copy.meal_summary}</p>
          )}
          {scaledItems.map((item, i) => (Number(item.qty) === 0 ? null : (
            <span
              key={`${item.name}-${i}`}
              data-testid={`chip-${i}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                border: '1px solid #ccc',
                borderRadius: 16,
                padding: '4px 8px',
                margin: '0 6px 6px 0',
                fontSize: T.font.sm,
              }}
            >
              <button
                type="button"
                onClick={() => bumpQty(i, -1)}
                disabled={busy}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 16, lineHeight: 1 }}
                aria-label={`decrease ${item.name}`}
              >
                −
              </button>
              <span>
                <input
                  inputMode="decimal"
                  value={qtys[i] != null ? qtys[i] : item.qty}
                  onChange={(e) => setQty(i, e.target.value)}
                  disabled={busy}
                  aria-label={`amount of ${item.name}`}
                  style={{ width: 40, fontSize: T.font.sm, textAlign: 'right' }}
                />
                {' '}× {item.name}
                <span style={{ color: T.color.textLight }}> · {item.portion_label}</span>
                {item.source === 'mine' ? (
                  <span style={{ color: T.color.textLight }}> · mine</span>
                ) : (item.estimated || item.source === 'grok_estimate') ? (
                  <span style={{ color: T.color.danger }} aria-label="estimate"> · ⚠</span>
                ) : null}
                {' '}
                <button
                  type="button"
                  onClick={() => setLabelFor(i)}
                  style={{ border: 'none', background: 'none', color: T.color.link, cursor: 'pointer', padding: 0, fontSize: 11 }}
                  aria-label={`fix ${item.name}`}
                >
                  fix
                </button>
                <span style={{ display: 'block', fontSize: 11, color: T.color.textLight }}>
                  {Math.round(item.kcal || 0)} kcal · {roundNutrient(item.pro_g)} g protein · {roundNutrient(item.cho_g)} g carbs
                </span>
              </span>
              <button
                type="button"
                onClick={() => bumpQty(i, 1)}
                disabled={busy}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 16, lineHeight: 1 }}
                aria-label={`increase ${item.name}`}
              >
                +
              </button>
              <button
                type="button"
                onClick={() => removeItem(i)}
                disabled={busy}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 4px', fontSize: 14, lineHeight: 1, color: T.color.textLight }}
                aria-label={`remove ${item.name}`}
              >
                ×
              </button>
            </span>
          )))}
          {labelFor != null && estimate.items?.[labelFor] && (
            <LabelEditor
              key={labelFor}
              api={api}
              item={estimate.items[labelFor]}
              onSaved={async () => {
                setLabelFor(null);
                await runEstimate(false);
              }}
              onCancel={() => setLabelFor(null)}
            />
          )}
          <p style={{ fontSize: 14 }}>
            {Math.round(scaledTotals.kcal)} kcal · {roundNutrient(scaledTotals.cho_g)} g carbs ·{' '}
            {roundNutrient(scaledTotals.pro_g)} g protein · {roundNutrient(scaledTotals.fat_g)} g fat
          </p>
          {(estimate.gaps || []).filter((g) => g.status === 'incomplete').map((g) => (
            <p key={g.note} style={{ fontSize: T.font.sm, margin: '4px 0' }}>{g.note}</p>
          ))}
          {(estimate.unmatched || []).length > 0 && (
            <p style={{ fontSize: T.font.sm, color: T.color.dangerMuted, marginBottom: 6 }}>
              Not found: {estimate.unmatched.join(', ')}. These aren't counted until you match or remove them.
            </p>
          )}
          {(estimate.approximate_candidates || []).map((raw) => (
            <p key={`approx-${raw}`} style={{ fontSize: T.font.sm, color: T.color.textMuted, margin: '4px 0' }}>
              Home-cooked?{' '}
              <button type="button" onClick={() => approximate(raw)} disabled={busy}>
                Break down “{raw}”
              </button>
            </p>
          ))}
          {(estimate.suggestions || []).map((s) => (
            s.options && s.options.length ? (
              <p key={s.raw} style={{ fontSize: T.font.sm, color: T.color.textMuted, margin: '4px 0' }}>
                “{s.raw}” →{' '}
                {s.options.map((opt) => (
                  <button
                    key={`${s.raw}-${opt.name}`}
                    type="button"
                    onClick={() => acceptSuggestion(s.raw, opt.name)}
                    disabled={busy}
                    style={{
                      marginRight: 6,
                      marginBottom: 4,
                      border: '1px solid #bbb',
                      borderRadius: 12,
                      background: '#f7f7f7',
                      cursor: 'pointer',
                      fontSize: T.font.xs,
                      padding: '2px 8px',
                    }}
                  >
                    {opt.name} · {opt.portion_label}
                  </button>
                ))}
              </p>
            ) : null
          ))}
        </div>
      )}
      {meals.length > 0 && (
        <ul style={{ paddingLeft: 18, fontSize: T.font.sm }}>
          {meals.map((m) => (
            <li key={m.id}>
              {mealTime(m.logged_at)}
              {RUN_CONTEXTS[m.context] ? ` · ${RUN_CONTEXTS[m.context]}` : ''}: {m.raw_text.slice(0, 80)}
              {m.pending ? ' · pending' : ''}
              {!m.pending && m.totals?.kcal != null ? ` · ${Math.round(m.totals.kcal)} kcal` : ''}
              {!m.pending && m.incomplete ? ' · some items unmatched' : ''}
              {m.logged_by && m.logged_by.startsWith('token:') ? ` · via ${m.logged_by.split(':').slice(2).join(':') || m.logged_by.slice(6)}` : ''}
              {m.pending && m.id !== approx?.id ? (
                <>
                  {' '}
                  <button
                    type="button"
                    onClick={() => setApprox({ id: m.id, card: m.card })}
                    style={{ border: 'none', background: 'none', color: T.color.link, cursor: 'pointer' }}
                  >
                    Review
                  </button>
                </>
              ) : null}
              {' '}
              <button type="button" onClick={() => removeMeal(m.id)} style={{ border: 'none', background: 'none', color: T.color.link, cursor: 'pointer' }}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}

      <h3>Next workout fuel</h3>
      {suggestions.length === 0 ? (
        <p style={{ color: T.color.textLight, fontSize: T.font.sm }}>No hard or long run in the next 36 hours.</p>
      ) : suggestions.map((w) => (
        <div key={`${w.source}-${w.start}`} style={{ fontSize: 14, marginBottom: 12 }}>
          <p style={{ margin: '0 0 6px' }}>
            Next: {(w.type || '').replace(/^./, (c) => c.toUpperCase())}
            {w.duration_h ? ` · ${w.duration_h} h` : ''}
            {w.start ? ` · ${formatStart(w.start)}` : ''}
            {w.weather?.temp_f != null ? ` · ${Math.round(w.weather.temp_f)}°F` : ''}
          </p>
          {w.pre && (
            <p style={{ margin: '0 0 4px' }}>
              Pre: ~{w.pre.cho_g[0]}–{w.pre.cho_g[1]} g carbs, keep fat/fiber moderate if under 90 min.
              {w.remaining_cho_g != null ? ` Daily remaining carbs: ${w.remaining_cho_g} g.` : ''}
            </p>
          )}
          {w.during && (
            <p style={{ margin: 0 }}>
              During: fluid ~{w.during.fluid_ml_h} ml/h · sodium ~{w.during.sodium_mg_h} mg/h
              {!(w.during.schedule || []).some((e) => e.action === 'gel')
                ? ' · no gel required at this duration'
                : ` · ${w.during.carb_g_h} g carbs/h`}
            </p>
          )}
          {(w.during?.schedule || []).filter((e) => e.action === 'gel' && e.cho_g >= 15).map((e) => (
            <span key={e.minute} style={{ display: 'block', fontSize: T.font.sm, color: T.color.textMuted }}>
              :{String(e.minute).padStart(2, '0')} gel ~{e.cho_g} g
            </span>
          ))}
        </div>
      ))}

      <p>
        <button type="button" onClick={() => setShowOverrides((o) => !o)} style={{ border: 'none', background: 'none', color: T.color.link, cursor: 'pointer', padding: 0 }}>
          {showOverrides ? 'Hide custom preview' : 'Custom fuel preview'}
        </button>
      </p>
      {showOverrides && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <label>Mass (kg)<input type="number" value={massKg} onChange={(e) => setMassKg(e.target.value)} style={{ display: 'block', width: 80 }} /></label>
          <label>Duration (h)<input type="number" step="0.25" value={durationH} onChange={(e) => setDurationH(e.target.value)} style={{ display: 'block', width: 80 }} /></label>
          <label>
            Workout type
            <select value={workoutType} onChange={(e) => setWorkoutType(e.target.value)} style={{ display: 'block' }}>
              <option value="easy">Easy</option>
              <option value="long">Long</option>
              <option value="quality">Quality</option>
              <option value="race">Race</option>
            </select>
          </label>
          <label>
            Gut tolerance
            <select value={gut} onChange={(e) => setGut(e.target.value)} style={{ display: 'block' }}>
              <option value="low">Low</option>
              <option value="med">Medium</option>
              <option value="high">High</option>
            </select>
          </label>
        </div>
      )}
      {error && <p style={{ color: T.color.danger, fontSize: T.font.sm }}>{String(error)}</p>}
    </CollapsibleCard>
  );
}
